import type { AttachmentMessageBody, ChatSessionMessage } from '@/types/messages.ts';

/**
 * A stored message as one line of text.
 *
 * The body is a tagged union the backend keeps growing, so an unknown kind
 * falls back to naming itself rather than rendering blank — a transcript with
 * a hole in it reads as a bug in the transcript.
 */
export function chatSessionMessageText(message: ChatSessionMessage): string {
  const body = message.body as unknown as Record<string, unknown>;

  if (body.type === 'text' && typeof body.text === 'string') return body.text;
  if (body.type === 'button' && typeof body.text === 'string') return body.text;

  if (body.type === 'template') {
    const template = body.template as Record<string, unknown> | undefined;
    if (template && typeof template.body === 'string') return template.body;
  }

  if (body.type === 'attachment') {
    const attachment = body as unknown as AttachmentMessageBody;
    const names = attachment.files?.length
      ? attachment.files.flatMap((file) => file.file_names ?? file.file_name ?? [])
      : [attachment.file_name];
    return attachment.caption || `[Attachment: ${names.filter(Boolean).join(', ')}]`;
  }

  if (body.type === 'card') {
    const card = body.card as Record<string, unknown> | undefined;
    return (card?.body as string) || '[Card]';
  }

  if (body.type === 'link') return (body.text as string) || '[Link]';
  if (body.type === 'notification') return (body.text as string) || '[Notification]';

  return `[${String(body.type)}]`;
}
